/**
 * Simulação de importação por ENCOMENDA (CVF importa e revende ao cliente com NF de saída).
 * Espelho da planilha "Calculo NF ALFA ENCOMENDA ... - REDUÇÃO ICMS ST" (abas Memória de Cálculo / Resumo).
 * Valores de importação na moeda da operação (USD); NF de saída e ST em R$ (= moeda × taxa fiscal).
 * Alíquotas em % (ex.: 4 = 4%).
 */

export interface EncomendaParams {
  /** Margem de contribuição líquida sobre o preço de venda. */
  marginRate: number;
  /** ICMS destacado na NF (interestadual importado). */
  icmsRate: number;
  /** % efetivo de ICMS sobre a venda (TTD 410) — só no resumo. */
  ttdRate: number;
  /** PIS/COFINS sobre a venda. */
  pisRate: number;
  cofinsRate: number;
  /** IRPJ/CSLL embutido no preço (gross-up da margem). */
  incomeTaxRate: number;
  /** Desconto de ICMS concedido no preço (% do ICMS). */
  icmsDiscountRate: number;
  /** IRPJ e CSLL sobre a margem bruta (resumo). */
  irpjRate: number;
  csllRate: number;
  /** Custo financeiro: juros ao mês e prazos (meses). */
  monthlyInterestRate: number;
  downPaymentShare: number;
  downPaymentMonths: number;
  balanceMonths: number;
  nationalizationMonths: number;
}

export const defaultEncomendaParams: EncomendaParams = {
  marginRate: 0.5,
  icmsRate: 4,
  ttdRate: 4,
  pisRate: 2,
  cofinsRate: 9.5,
  incomeTaxRate: 34,
  icmsDiscountRate: 0,
  irpjRate: 25,
  csllRate: 9,
  monthlyInterestRate: 0,
  downPaymentShare: 12.66,
  downPaymentMonths: 0,
  balanceMonths: 0,
  nationalizationMonths: 0,
};

/** ICMS DI da planilha (alíquota fixa de 4% sobre a base da NF). */
const ICMS_DI_RATE = 0.04;

export interface EncomendaItemInput {
  quantity: number;
  unitValue: number;
  unitWeight: number;
  iiRate: number;
  ipiRate: number;
  pisRate: number;
  cofinsRate: number;
  /** Antidumping da linha (moeda da operação, total). */
  dumping: number;
  /** % de redução da base do ICMS ST (Conv. ICMS 21/13); null = sem redução. */
  icmsReductionRate: number | null;
  /** ICMS destacado na NF do item (%); vazio = parâmetro "ICMS destacado". */
  icmsRate?: number | null;
  /** MVA / MVA ajustada (%); null = sem ST para o item. */
  mvaRate: number | null;
  /** Alíquota interna do ICMS no estado de destino (%). */
  internalIcmsRate: number;
  /** Frete rodoviário rateado na linha (R$), somado à NF de saída. */
  roadFreightBrl: number;
  /** DIFAL (%): interna do destino, interestadual e FCP do destino. Sem interna = sem DIFAL. */
  difalInternalRate?: number | null;
  difalInterstateRate?: number | null;
  difalFcpRate?: number | null;
  /** IBS (UF + município) e CBS (%), destacados na NF (fora do total em 2026). */
  ibsRate?: number | null;
  cbsRate?: number | null;
}

export interface EncomendaInput {
  exchangeRate: number;
  freight: number;
  insuranceBrl: number;
  prepaidFees: number;
  /** Despesas aduaneiras em R$ (rateadas pelo peso). */
  expensesBrl: number;
  /** Pagamentos de câmbio já feitos (valor na moeda × taxa). Vazio = sem diferencial cambial. */
  payments: { amount: number; rate: number }[];
  items: EncomendaItemInput[];
  params: EncomendaParams;
}

export interface EncomendaItemResult {
  fob: number;
  freight: number;
  insurance: number;
  prepaid: number;
  cif: number;
  ii: number;
  ipi: number;
  pis: number;
  cofins: number;
  expenses: number;
  disbursement: number;
  finalCost: number;
  interest: number;
  effectiveIpiRate: number;
  salePrice: number;
  saleIpi: number;
  /** Preço sem IPI por unidade (R$). */
  unitPriceNoIpiBrl: number;
  /** Total dos produtos na NF (base do IPI, R$). */
  productsBrl: number;
  ipiNfBrl: number;
  icmsBaseBrl: number;
  icmsBrl: number;
  pisNfBrl: number;
  cofinsNfBrl: number;
  stBaseBrl: number;
  /** ICMS ST (já com redução, se houver). */
  stBrl: number;
  /** DIFAL + FCP do destino (recolhido à parte, fora da NF). */
  difalBrl: number;
  ibsBrl: number;
  cbsBrl: number;
  /** NF de saída: produtos + IPI + ST + frete rodoviário. */
  invoiceBrl: number;
  unitStBrl: number;
  /** Preço unitário com IPI + ST (R$). */
  unitTotalBrl: number;
}

export interface EncomendaTotals {
  fob: number;
  cif: number;
  taxes: number;
  expenses: number;
  disbursement: number;
  finalCost: number;
  interest: number;
  exchangeDifferenceBrl: number;
  salePrice: number;
  saleIpi: number;
  icmsBaseBrl: number;
  icmsHighlighted: number;
  icmsEffective: number;
  pisSale: number;
  cofinsSale: number;
  incomeTaxBase: number;
  incomeTax: number;
  stBrl: number;
  difalBrl: number;
  ibsBrl: number;
  cbsBrl: number;
  icmsDiscount: number;
  grossMargin: number;
  roadFreightBrl: number;
  invoiceWithStBrl: number;
  irpj: number;
  csll: number;
  contributionMargin: number;
}

export interface EncomendaResult {
  items: EncomendaItemResult[];
  totals: EncomendaTotals;
}

const pct = (value: number) => value / 100;

/** IPI "por dentro" da planilha: 8 iterações de x = (1 − x)·t (converge para t / (1 + t)). */
export const effectiveIpi = (rate: number) => {
  let x = (1 - rate) * rate;
  for (let i = 0; i < 7; i++) x = (1 - x) * rate;
  return x;
};

export const calculateEncomenda = (input: EncomendaInput): EncomendaResult => {
  const { exchangeRate: fx, params: p } = input;
  const totalFob = input.items.reduce((sum, i) => sum + i.quantity * i.unitValue, 0);
  const totalKg = input.items.reduce((sum, i) => sum + i.quantity * i.unitWeight, 0);
  const insurance = fx > 0 ? input.insuranceBrl / fx : 0;
  const paidBrl = input.payments.reduce((sum, pay) => sum + pay.amount * pay.rate, 0);
  // Planilha: diferencial = pagamentos em R$ − FOB × taxa fiscal (só quando há pagamento informado);
  // entra nas despesas aduaneiras, rateadas pelo peso
  const exchangeDifferenceBrl = paidBrl > 0 ? paidBrl - totalFob * fx : 0;
  const expensesTotal = fx > 0 ? (input.expensesBrl + exchangeDifferenceBrl) / fx : 0;

  const items = input.items.map((i): EncomendaItemResult => {
    const fob = i.quantity * i.unitValue;
    const valueShare = totalFob > 0 ? fob / totalFob : 0;
    const weightShare = totalKg > 0 ? (i.quantity * i.unitWeight) / totalKg : 0;

    const freight = weightShare * input.freight;
    const itemInsurance = valueShare * insurance;
    const prepaid = valueShare * input.prepaidFees;
    const cif = fob + freight + itemInsurance + prepaid;
    const ii = cif * pct(i.iiRate);
    const ipi = (cif + ii) * pct(i.ipiRate);
    const pis = cif * pct(i.pisRate);
    const cofins = cif * pct(i.cofinsRate);
    const expenses = expensesTotal * weightShare;
    const disbursement = cif + ii + ipi + pis + cofins + expenses + i.dumping;
    const finalCost = disbursement - ipi - pis - cofins;

    // Custo financeiro (juros compostos ao mês sobre down payment, saldo e nacionalização)
    const r = pct(p.monthlyInterestRate);
    const downShare = pct(p.downPaymentShare);
    const grow = (value: number, months: number) => value * (1 + r) ** months - value;
    const interest =
      grow(cif * downShare, p.downPaymentMonths) +
      grow(cif * (1 - downShare), p.balanceMonths) +
      grow(disbursement - fob, p.nationalizationMonths);

    const cost = finalCost + interest;
    const icms = pct(i.icmsRate ?? p.icmsRate);
    const effectiveIpiRate = effectiveIpi(pct(i.ipiRate));
    const icmsShare = (1 - effectiveIpiRate) * icms;
    const salePis = (1 - effectiveIpiRate - icmsShare) * pct(p.pisRate);
    const saleCofins = (1 - effectiveIpiRate - icmsShare) * pct(p.cofinsRate);
    const margin = pct(p.marginRate);
    const incomeTax = margin / (1 - pct(p.incomeTaxRate)) - margin;
    const totalRate = margin + icmsShare + effectiveIpiRate + salePis + saleCofins + incomeTax;
    const salePrice = totalRate < 1 ? cost / (1 - totalRate) : 0;
    const saleIpi = salePrice * effectiveIpiRate;

    // NF de saída (R$)
    const unitPriceNoIpiBrl = i.quantity > 0 ? ((salePrice - saleIpi) / i.quantity) * fx : 0;
    const productsBrl = unitPriceNoIpiBrl * i.quantity;
    const ipiNfBrl = productsBrl * pct(i.ipiRate);
    // ICMS próprio abatido da ST (planilha: 4% fixo = ICMS destacado de importado)
    const icmsDi = productsBrl * (i.icmsRate != null ? icms : ICMS_DI_RATE);
    const reduction = i.icmsReductionRate ? pct(i.icmsReductionRate) : 0;
    const reducedBase = productsBrl - productsBrl * reduction;
    // Na planilha a redução (Conv. ICMS 21/13) só reduz a base da ST; o ICMS próprio fica na base cheia
    const icmsBaseBrl = productsBrl;
    const pisCofinsBase = productsBrl - icmsDi;

    const mva = i.mvaRate != null ? pct(i.mvaRate) : null;
    const internal = pct(i.internalIcmsRate);
    let stBaseBrl = 0;
    let stBrl = 0;
    // Sem alíquota interna do destino não há base para ST (a conta viraria −ICMS DI)
    if (mva != null && internal > 0) {
      stBaseBrl = (reducedBase + ipiNfBrl) * (1 + mva);
      stBrl = stBaseBrl * internal - icmsDi;
    }

    // DIFAL base única: (produtos + IPI) × (interna − interestadual) + FCP do destino
    const difalInternal = pct(i.difalInternalRate ?? 0);
    const difalBrl = difalInternal > 0
      ? (productsBrl + ipiNfBrl) * (Math.max(difalInternal - pct(i.difalInterstateRate ?? 0), 0) + pct(i.difalFcpRate ?? 0))
      : 0;

    const unitStBrl = i.quantity > 0 ? stBrl / i.quantity : 0;
    const unitSaleBrl = unitPriceNoIpiBrl + unitPriceNoIpiBrl * pct(i.ipiRate);

    return {
      fob, freight, insurance: itemInsurance, prepaid, cif, ii, ipi, pis, cofins, expenses,
      disbursement, finalCost, interest, effectiveIpiRate, salePrice, saleIpi,
      unitPriceNoIpiBrl, productsBrl, ipiNfBrl, icmsBaseBrl, icmsBrl: icmsBaseBrl * icms,
      pisNfBrl: pisCofinsBase * pct(p.pisRate), cofinsNfBrl: pisCofinsBase * pct(p.cofinsRate),
      stBaseBrl, stBrl, difalBrl,
      ibsBrl: productsBrl * pct(i.ibsRate ?? 0),
      cbsBrl: productsBrl * pct(i.cbsRate ?? 0),
      invoiceBrl: productsBrl + ipiNfBrl + stBrl + i.roadFreightBrl,
      unitStBrl,
      unitTotalBrl: unitSaleBrl + unitStBrl,
    };
  });

  const sum = (pick: (i: EncomendaItemResult) => number) => items.reduce((s, i) => s + pick(i), 0);
  const fob = sum((i) => i.fob);
  const cif = sum((i) => i.cif);
  const taxes = sum((i) => i.ii + i.ipi + i.pis + i.cofins) + input.items.reduce((s, i) => s + i.dumping, 0);
  const disbursement = sum((i) => i.disbursement);
  const finalCost = sum((i) => i.finalCost);
  const salePrice = sum((i) => i.salePrice);
  const saleIpi = sum((i) => i.saleIpi);
  const icmsBaseBrl = sum((i) => i.icmsBaseBrl);
  const icmsBase = fx > 0 ? icmsBaseBrl / fx : 0;
  const icmsHighlighted = fx > 0 ? sum((i) => i.icmsBrl) / fx : 0;
  const icmsEffective = icmsBase * pct(p.ttdRate);
  const pisSale = fx > 0 ? sum((i) => i.pisNfBrl) / fx : 0;
  const cofinsSale = fx > 0 ? sum((i) => i.cofinsNfBrl) / fx : 0;
  const incomeTaxBase = salePrice - icmsEffective - saleIpi - pisSale - cofinsSale - finalCost;
  const stBrl = sum((i) => i.stBrl);
  const roadFreightBrl = input.items.reduce((s, i) => s + i.roadFreightBrl, 0);
  const grossMargin = salePrice - finalCost - icmsHighlighted - saleIpi - pisSale - cofinsSale;
  const irpj = grossMargin * pct(p.irpjRate);
  const csll = grossMargin * pct(p.csllRate);

  return {
    items,
    totals: {
      fob,
      cif,
      taxes,
      expenses: expensesTotal,
      disbursement,
      finalCost,
      interest: sum((i) => i.interest),
      exchangeDifferenceBrl,
      salePrice,
      saleIpi,
      icmsBaseBrl,
      icmsHighlighted,
      icmsEffective,
      pisSale,
      cofinsSale,
      incomeTaxBase,
      incomeTax: incomeTaxBase * pct(p.incomeTaxRate),
      stBrl,
      difalBrl: sum((i) => i.difalBrl),
      ibsBrl: sum((i) => i.ibsBrl),
      cbsBrl: sum((i) => i.cbsBrl),
      // Desconto sobre o ICMS do preço de venda (alíquota média ponderada dos itens)
      icmsDiscount: icmsBase > 0 ? salePrice * (icmsHighlighted / icmsBase) * pct(p.icmsDiscountRate) : 0,
      grossMargin,
      roadFreightBrl,
      invoiceWithStBrl: salePrice * fx + stBrl + roadFreightBrl,
      irpj,
      csll,
      contributionMargin: grossMargin - irpj - csll,
    },
  };
};
